import { useEffect, useState, createContext, useContext, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import WorkerDashboard from "./pages/WorkerDashboard";
import TeamManagerDashboard from "./pages/TeamManagerDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import ProfilePage from "./pages/ProfilePage";
import TaskDetailPage from "./pages/TaskDetailPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfUsePage from "./pages/TermsOfUsePage";
import CookieDeclarationPage from "./pages/CookieDeclarationPage";
import ContactUsPage from "./pages/ContactUsPage";
import AboutUsPage from "./pages/AboutUsPage";
import SupportPage from "./pages/SupportPage";
import AdvertisePage from "./pages/AdvertisePage";
import HowToEarnPage from "./pages/HowToEarnPage";
import MaximizeEarningsPage from "./pages/MaximizeEarningsPage";
import PointsPaymentsPage from "./pages/PointsPaymentsPage";
import FAQPage from "./pages/FAQPage";
import DataSecurityPage from "./pages/DataSecurityPage";
import FraudPreventionPage from "./pages/FraudPreventionPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import CaliforniaPrivacyPage from "./pages/CaliforniaPrivacyPage";
import SMSTermsPage from "./pages/SMSTermsPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  const checkAuth = async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/v2/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      console.error("Auth check failed:", error);
      localStorage.removeItem("token");
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/v2/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, role, additionalData = {}) => {
    const response = await axios.post(`${API}/v2/auth/register`, { 
      email, 
      password, 
      first_name: additionalData.first_name || name.split(' ')[0],
      last_name: additionalData.last_name || name.split(' ').slice(1).join(' ') || 'User',
      country: additionalData.country || 'Unknown',
      social_profiles: additionalData.social_profiles || []
    });
    // New flow: registration returns requires_verification instead of token
    // User must verify email before logging in
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/v2/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
    } catch (e) {
      console.error("Logout error:", e);
    }
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, token, updateUser, setUser, setToken }}>
      {children}
    </AuthContext.Provider>
  );
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, setToken } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash;
    const sessionId = new URLSearchParams(hash.replace('#', '')).get('session_id');

    if (sessionId) {
      axios.post(`${API}/v2/auth/session`, { session_id: sessionId }, { withCredentials: true })
        .then(response => {
          const userData = response.data.user;
          setUser(userData);
          // Navigate based on role
          if (userData.role === 'admin') {
            navigate('/admin', { replace: true, state: { user: userData } });
          } else if (userData.role === 'team_manager') {
            navigate('/team-manager', { replace: true, state: { user: userData } });
          } else {
            navigate('/dashboard', { replace: true, state: { user: userData } });
          }
        })
        .catch(error => {
          console.error("Session exchange failed:", error);
          navigate('/login', { replace: true });
        });
    } else {
      navigate('/login', { replace: true });
    }
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent mx-auto mb-4"></div>
        <p className="text-slate-600">Authenticating...</p>
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    if (user.role === 'team_manager') return <Navigate to="/team-manager" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id - detect during render, not in useEffect
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={['worker']}>
          <WorkerDashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/team-manager" element={
        <ProtectedRoute allowedRoles={['team_manager', 'admin']}>
          <TeamManagerDashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      
      <Route path="/task/:taskId" element={
        <ProtectedRoute>
          <TaskDetailPage />
        </ProtectedRoute>
      } />

      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/california-privacy" element={<CaliforniaPrivacyPage />} />
      <Route path="/sms-terms" element={<SMSTermsPage />} />
      <Route path="/terms-of-use" element={<TermsOfUsePage />} />
      <Route path="/cookie-declaration" element={<CookieDeclarationPage />} />
      <Route path="/contact-us" element={<ContactUsPage />} />
      <Route path="/advertise" element={<AdvertisePage />} />
      <Route path="/about-us" element={<AboutUsPage />} />
      <Route path="/how-to-earn" element={<HowToEarnPage />} />
      <Route path="/maximize-earnings" element={<MaximizeEarningsPage />} />
      <Route path="/points-payments" element={<PointsPaymentsPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/data-security" element={<DataSecurityPage />} />
      <Route path="/fraud-prevention" element={<FraudPreventionPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      
      <Route path="/support" element={
        <ProtectedRoute>
          <SupportPage />
        </ProtectedRoute>
      } />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" />
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
